import { FC, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { QrCode as QrCodeIcon } from '@mui/icons-material';
import { tokens } from "@/tokens";
import { useAppSelector } from '@/store';
import { useGetHTSCodesQuery } from '@/services/dashboard/sm-api';
import { useHTSDescriptions } from '@/context/hts-descriptions-context';
import { formatHTSCode } from '@/utils';
import { roles } from '@/roles';
import { tariff_code_placeholder } from '@/constants';
import UnitCostAnalysisSection from '../product-catalogue/components/duty-calculator/UnitCostAnalysisSection';

interface HtsOption {
  hts_code: string;
  description?: string;
}

interface ClientHtsOption {
  hts_code: string;
  goods_value: number;
  duty_value?: number;
  formatted_hts_code: string;
}

type AnyHtsOption = HtsOption | ClientHtsOption;

const isClientHtsOption = (option: AnyHtsOption): option is ClientHtsOption => {
  return 'goods_value' in option;
};

const normalizeHtsCode = (code: string): string => {
  const normalized = code.replace(/\./g, '').replace(/^0+/, '');
  return normalized || code.replace(/\./g, '');
};

const UnitCostAnalysisView: FC = () => {
  const [htsSearchValue, setHtsSearchValue] = useState<HtsOption | string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const clientId = useMemo(() => {
    if (isTDAdminTCD) {
      return selectedTenant?._id;
    }
    if (selectedClient?._id) {
      return selectedClient._id;
    }
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?.tenantId;
    }
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  const { data: clientHtsCodes, isLoading: isLoadingClientCodes } = useGetHTSCodesQuery(
    { clientId: clientId || "" },
    { skip: !clientId }
  );

  const sortedClientHtsCodes: ClientHtsOption[] = useMemo(() => {
    if (!clientHtsCodes?.getHTSCodesByCountry) return [];

    return [...clientHtsCodes.getHTSCodesByCountry]
      .sort((a, b) => b.goods_value - a.goods_value)
      .map(code => ({
        ...code,
        formatted_hts_code: formatHTSCode(code.hts_code)
      }));
  }, [clientHtsCodes]);

  const filteredClientCodes = useMemo(() => {
    if (!searchTerm) return sortedClientHtsCodes;
    const normalizedSearch = normalizeHtsCode(searchTerm);
    return sortedClientHtsCodes.filter(code =>
      normalizeHtsCode(code.hts_code).includes(normalizedSearch) ||
      code.formatted_hts_code.includes(searchTerm)
    );
  }, [sortedClientHtsCodes, searchTerm]);

  const selectedHtsCode = typeof htsSearchValue === 'object' && htsSearchValue
    ? htsSearchValue.hts_code
    : (typeof htsSearchValue === 'string' ? htsSearchValue : undefined);

  const selectedHtsCodeForApi = selectedHtsCode?.replace(/\./g, '');

  // Fetch HTS description using context
  const { descriptions, fetchDescription } = useHTSDescriptions();

  useEffect(() => {
    if (selectedHtsCodeForApi) {
      fetchDescription(selectedHtsCodeForApi);
    }
  }, [selectedHtsCodeForApi, fetchDescription]);

  const htsDescription = descriptions[selectedHtsCodeForApi || ''];

  // Reset HTS selection when client changes
  useEffect(() => {
    setHtsSearchValue(null);
    setSearchTerm('');
  }, [clientId]);

  return (
    <Box sx={{ pb: 3, pr: 1 }}>
      <Box sx={{
        borderRadius: tokens.radius.lg,
        border: `1px solid ${tokens.border.container}`,
        bgcolor: '#fff',
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
      }}>
        {/* Header: Title + Search */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3, pt: 2.5, pb: 2 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, whiteSpace: 'nowrap' }}>
            Unit Cost Analysis
          </Typography>
          <Autocomplete<AnyHtsOption, false, false, true>
            freeSolo
            size="small"
            options={filteredClientCodes as AnyHtsOption[]}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (isClientHtsOption(option)) return option.formatted_hts_code;
              return option.hts_code || '';
            }}
            filterOptions={(x) => x}
            loading={isLoadingClientCodes}
            value={htsSearchValue as AnyHtsOption | string | null}
            inputValue={searchTerm}
            onInputChange={(_, newInputValue, reason) => {
              if (reason !== 'reset') {
                setSearchTerm(newInputValue);
              }
            }}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                const cleanCode = newValue.replace(/\./g, '');
                setHtsSearchValue({ hts_code: formatHTSCode(cleanCode) });
                setSearchTerm('');
              } else if (newValue && isClientHtsOption(newValue)) {
                setHtsSearchValue({ hts_code: newValue.formatted_hts_code });
                setSearchTerm('');
              } else {
                setHtsSearchValue(newValue);
                setSearchTerm('');
              }
            }}
            ListboxProps={{
              style: { maxHeight: 240 },
            }}
            renderOption={(props, option) => {
              const isClientCode = isClientHtsOption(option);
              const htsCode = isClientCode ? option.formatted_hts_code : option.hts_code;

              return (
                <Box component="li" {...props} key={isClientCode ? option.hts_code : option.hts_code.replace(/\./g, '')} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, minHeight: 0 }}>
                  <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ fontSize: tokens.fontSize.md }}>
                    {htsCode}
                  </Typography>
                  {isClientCode && (
                    <Chip
                      label={`$${(option.goods_value / 1000000).toFixed(1)}M`}
                      size="small"
                      sx={{
                        ml: 'auto',
                        fontSize: tokens.fontSize["2xs"],
                        fontWeight: tokens.fontWeight.bold,
                        bgcolor: 'rgba(11,73,75,0.08)',
                        color: tokens.brand.primary,
                        height: 22,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search HTS codes..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: tokens.radius.md,
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <QrCodeIcon sx={{ color: tokens.text.secondary, mr: 1, fontSize: 20 }} />
                  ),
                  endAdornment: (
                    <>
                      {isLoadingClientCodes ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              sortedClientHtsCodes.length === 0 ? "No HTS codes found for your imports" : "No matching HTS codes"
            }
            sx={{ flex: 1 }}
          />
        </Stack>

        {/* Content */}
        {selectedHtsCodeForApi ? (
          <UnitCostAnalysisSection
            htsCode={selectedHtsCodeForApi}
            clientId={clientId}
            htsCodeFormatted={selectedHtsCode}
            htsDescription={htsDescription?.description ?? undefined}
            isDescriptionLoading={htsDescription?.isLoading ?? undefined}
            embedded
          />
        ) : (
          <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 6 }}>
            <Box
              component="img"
              src={tariff_code_placeholder}
              alt=""
              sx={{ width: 120, height: 120, objectFit: 'contain', opacity: 0.85 }}
            />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.bold, color: tokens.text.body, mb: 0.5 }}>
                Select an HTS Code to Begin
              </Typography>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, maxWidth: 340, mx: 'auto' }}>
                Use the search field above to find an HTS code and view detailed unit cost analysis including historical trends, manufacturer data, and origin breakdowns.
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default UnitCostAnalysisView;
